import QRCode from 'qrcode';
import { collection, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase/client';

export type ReportVerificationInput = {
  userId: string;
  patientId: string | null;
  reportType: string;
  title: string;
  doctorName: string;
  doctorCrm: string;
  doctorCrmState: string;
  doctorRqe: string;
  doctorSpecialty: string;
  doctorSubspecialty: string;
  orthoId: string;
  patientName: string;
  anatomy: string | null;
  normalizedContent: string;
  issuedAt: Date;
  issuedAtFormatted: string;
  pdfVersion?: string;
  baseUrl?: string;
};

export type ReportVerificationRecord = {
  id: string;
  userId: string;
  patientId: string | null;
  reportType: string;
  title: string;
  doctorName: string;
  doctorCrm: string;
  doctorCrmState: string;
  doctorRqe: string;
  doctorSpecialty: string;
  doctorSubspecialty: string;
  orthoId: string;
  patientName: string;
  patientNameMasked: string;
  anatomy: string | null;
  issuedAtIso: string;
  issuedAtFormatted: string;
  verificationCode: string;
  verificationUrl: string;
  contentHash: string;
  status: 'valid' | 'revoked';
  pdfVersion: string;
  isRevoked: boolean;
  revokedAt: null | unknown;
  replacedBy: string | null;
  createdAt: unknown;
  updatedAt: unknown;
};

export type PublicReportVerificationRecord = Pick<
  ReportVerificationRecord,
  | 'userId'
  | 'id'
  | 'reportType'
  | 'title'
  | 'doctorName'
  | 'doctorCrm'
  | 'doctorCrmState'
  | 'doctorRqe'
  | 'doctorSpecialty'
  | 'doctorSubspecialty'
  | 'orthoId'
  | 'patientNameMasked'
  | 'anatomy'
  | 'issuedAtIso'
  | 'issuedAtFormatted'
  | 'verificationCode'
  | 'verificationUrl'
  | 'contentHash'
  | 'status'
  | 'pdfVersion'
  | 'isRevoked'
  | 'revokedAt'
  | 'replacedBy'
> & {
  createdAt: unknown;
  updatedAt: unknown;
};

export type ReportVerificationBundle = {
  documentId: string;
  verificationCode: string;
  verificationUrl: string;
  contentHash: string;
  issuedAtIso: string;
  privateRecord: ReportVerificationRecord;
  publicRecord: PublicReportVerificationRecord;
};

const normalizeReportText = (value: string) =>
  String(value || '')
    .replace(/\r/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeDocIdChunk = (value: string) =>
  value
    .replace(/[^a-z0-9]/gi, '')
    .toUpperCase()
    .trim();

const isLocalOrPrivateHost = (value: string) =>
  /(^|:\/\/)(localhost|127\.0\.0\.1|0\.0\.0\.0)(:\d+)?($|\/)/i.test(value) ||
  /:\/\/(10\.\d{1,3}\.\d{1,3}\.\d{1,3}|192\.168\.\d{1,3}\.\d{1,3}|172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3})(:\d+)?($|\/)/i.test(value);

const resolvePublicAppUrl = () => {
  const env = typeof import.meta !== 'undefined' ? (import.meta as any).env || {} : {};
  const candidates = [
    env.VITE_APP_URL,
    env.VITE_PUBLIC_APP_URL,
    env.APP_URL,
    env.PUBLIC_URL,
  ]
    .map((value) => normalizeReportText(String(value || '')))
    .filter(Boolean);

  const envBaseUrl = candidates.find((value) => !isLocalOrPrivateHost(value));
  if (envBaseUrl) {
    return envBaseUrl;
  }

  if (typeof window !== 'undefined') {
    const origin = normalizeReportText(window.location.origin);
    if (origin && !isLocalOrPrivateHost(origin)) {
      return origin;
    }
  }

  return 'https://orthoprecision.app';
};

const sha256Hex = async (value: string) => {
  const data = new TextEncoder().encode(value);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

const maskPatientName = (name: string) => {
  const clean = normalizeReportText(name);
  if (!clean) return '';

  const parts = clean.split(' ').filter(Boolean);
  if (parts.length === 1) {
    const first = parts[0];
    return `${first.slice(0, 2).toUpperCase()}***`;
  }

  return parts
    .slice(0, 2)
    .map((part) => `${part.charAt(0).toUpperCase()}.`)
    .join(' ');
};

export const buildVerificationUrl = (documentId: string, baseUrl?: string) => {
  const candidateRoot = normalizeReportText(baseUrl || '');
  const root = candidateRoot && !isLocalOrPrivateHost(candidateRoot) ? candidateRoot : resolvePublicAppUrl();
  return `${root.replace(/\/+$/, '')}/verify-report/${encodeURIComponent(documentId)}`;
};

export const generateVerificationCode = async (documentId: string, issuedAt: Date) => {
  const yyyymmdd = `${issuedAt.getFullYear()}${String(issuedAt.getMonth() + 1).padStart(2, '0')}${String(issuedAt.getDate()).padStart(2, '0')}`;
  const digest = await sha256Hex(`${documentId}|${issuedAt.toISOString()}`);
  const numericChunk = BigInt(`0x${digest.slice(0, 12)}`).toString(36).toUpperCase();
  const shortChunk = normalizeDocIdChunk(numericChunk).slice(0, 6).padEnd(6, '0');
  return `OP-${yyyymmdd}-${shortChunk}`;
};

export const generateQrCodeDataUrl = async (verificationUrl: string) =>
  QRCode.toDataURL(verificationUrl, {
    errorCorrectionLevel: 'M',
    margin: 1,
    width: 480,
    color: {
      dark: '#0f172a',
      light: '#ffffff',
    },
  });

export const buildReportVerificationBundle = async (
  input: ReportVerificationInput
): Promise<ReportVerificationBundle> => {
  const documentId = doc(collection(db, 'generatedReports')).id;
  const issuedAtIso = input.issuedAt.toISOString();
  const verificationCode = await generateVerificationCode(documentId, input.issuedAt);
  const verificationUrl = buildVerificationUrl(documentId, input.baseUrl);

  const contentHash = await sha256Hex(
    JSON.stringify({
      reportType: normalizeReportText(input.reportType),
      doctorName: normalizeReportText(input.doctorName),
      doctorCrm: normalizeReportText(input.doctorCrm),
      doctorCrmState: normalizeReportText(input.doctorCrmState),
      doctorRqe: normalizeReportText(input.doctorRqe),
      doctorSpecialty: normalizeReportText(input.doctorSpecialty),
      doctorSubspecialty: normalizeReportText(input.doctorSubspecialty),
      orthoId: normalizeReportText(input.orthoId),
      patientName: normalizeReportText(input.patientName),
      anatomy: normalizeReportText(input.anatomy || ''),
      issuedAtIso,
      normalizedContent: normalizeReportText(input.normalizedContent),
    })
  );

  const sharedFields = {
    id: documentId,
    userId: input.userId,
    patientId: input.patientId,
    reportType: normalizeReportText(input.reportType),
    title: normalizeReportText(input.title),
    doctorName: normalizeReportText(input.doctorName),
    doctorCrm: normalizeReportText(input.doctorCrm),
    doctorCrmState: normalizeReportText(input.doctorCrmState).toUpperCase(),
    doctorRqe: normalizeReportText(input.doctorRqe),
    doctorSpecialty: normalizeReportText(input.doctorSpecialty),
    doctorSubspecialty: normalizeReportText(input.doctorSubspecialty),
    orthoId: normalizeReportText(input.orthoId),
    anatomy: normalizeReportText(input.anatomy || '') || null,
    issuedAtIso,
    issuedAtFormatted: normalizeReportText(input.issuedAtFormatted),
    verificationCode,
    verificationUrl,
    contentHash,
    status: 'valid' as const,
    pdfVersion: input.pdfVersion || 'orthoprecision-report-v1',
    isRevoked: false,
    revokedAt: null,
    replacedBy: null,
  };

  const privateRecord: ReportVerificationRecord = {
    ...sharedFields,
    patientName: normalizeReportText(input.patientName),
    patientNameMasked: maskPatientName(input.patientName),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const publicRecord: PublicReportVerificationRecord = {
    userId: input.userId,
    id: sharedFields.id,
    reportType: sharedFields.reportType,
    title: sharedFields.title,
    doctorName: sharedFields.doctorName,
    doctorCrm: sharedFields.doctorCrm,
    doctorCrmState: sharedFields.doctorCrmState,
    doctorRqe: sharedFields.doctorRqe,
    doctorSpecialty: sharedFields.doctorSpecialty,
    doctorSubspecialty: sharedFields.doctorSubspecialty,
    orthoId: sharedFields.orthoId,
    patientNameMasked: maskPatientName(input.patientName),
    anatomy: sharedFields.anatomy,
    issuedAtIso: sharedFields.issuedAtIso,
    issuedAtFormatted: sharedFields.issuedAtFormatted,
    verificationCode: sharedFields.verificationCode,
    verificationUrl: sharedFields.verificationUrl,
    contentHash: sharedFields.contentHash,
    status: sharedFields.status,
    pdfVersion: sharedFields.pdfVersion,
    isRevoked: sharedFields.isRevoked,
    revokedAt: sharedFields.revokedAt,
    replacedBy: sharedFields.replacedBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  return {
    documentId,
    verificationCode,
    verificationUrl,
    contentHash,
    issuedAtIso,
    privateRecord,
    publicRecord,
  };
};

export const saveGeneratedReportRecord = async (bundle: ReportVerificationBundle) => {
  const privatePath = `users/${bundle.privateRecord.userId}/generatedReports/${bundle.documentId}`;
  const publicPath = `publicGeneratedReports/${bundle.documentId}`;

  if (import.meta.env.DEV) {
    console.log('[ConsultModule] auth.currentUser?.uid:', bundle.privateRecord.userId);
    console.log('[ConsultModule] operation payload:', {
      privateRecord: bundle.privateRecord,
      publicRecord: bundle.publicRecord,
    });
    console.log('[ConsultModule] firestore path:', privatePath, publicPath);
  }

  await Promise.all([
    setDoc(doc(db, 'users', bundle.privateRecord.userId, 'generatedReports', bundle.documentId), bundle.privateRecord),
    setDoc(doc(db, 'publicGeneratedReports', bundle.documentId), bundle.publicRecord),
  ]);
};

export const fetchPublicGeneratedReport = async (documentId: string) => {
  const snap = await getDoc(doc(db, 'publicGeneratedReports', documentId));
  if (!snap.exists()) return null;
  return snap.data() as Partial<PublicReportVerificationRecord> & { id?: string };
};
